import { useEffect, useRef } from 'react';
import { useMixpanel } from '@/hooks/useMixpanel';
import {
  DEALS_MIXPANEL_EVENTS,
  type DealCreationCrmSearchedPayload,
} from '../utils/mixpanelEvents';

interface CrmSearchTracking {
  recordType: DealCreationCrmSearchedPayload['record_type'];
  /** The term the lookup ran on, empty until the BD searched. */
  searched: string;
  isFetching: boolean;
  resultCount: number;
}

/**
 * Report a CRM lookup once it has answered.
 *
 * The lookup takes an explicit act and its result is what sends the BD to "Create new", so both
 * halves matter — which is why the emit waits for the answer rather than firing on the click. One
 * per settled term: the query re-renders the step, and a repeated reading is the same lookup.
 */
export function useTrackCrmSearch({
  recordType,
  searched,
  isFetching,
  resultCount,
}: CrmSearchTracking) {
  const trackAction = useMixpanel((s) => s.trackAction);
  const reportedTerm = useRef<string | null>(null);

  useEffect(() => {
    if (searched === '' || isFetching || reportedTerm.current === searched) return;
    reportedTerm.current = searched;
    const payload: DealCreationCrmSearchedPayload = {
      record_type: recordType,
      result_count: resultCount,
      matched: resultCount > 0,
    };
    trackAction(DEALS_MIXPANEL_EVENTS.CREATION_CRM_SEARCHED, payload);
  }, [searched, isFetching, resultCount, recordType, trackAction]);
}
